import { getEvidenceById } from './evidence';

/**
 * Lógica central da calculadora Mini Mental (MEEM)
 * Referências clínicas vêm de src/lib/evidence.js para evitar números duplicados.
 */

export const LICENSED_MMSE_ITEMS_ENABLED = false;

export const DOMAINS_CONFIG = [
  { id: 'temporalOrientation', label: 'Orientação temporal', max: 5, help: 'Dia da semana, dia do mês, mês, ano e hora aproximada.' },
  { id: 'spatialOrientation', label: 'Orientação espacial', max: 5, help: 'Local exato, instituição/ponto de referência, bairro/cidade, estado e país.' },
  { id: 'registration', label: 'Registro', max: 3, help: 'Repetição imediata de 3 palavras não relacionadas.' },
  { id: 'attentionCalculation', label: 'Atenção e cálculo', max: 5, help: 'Cálculos sequenciais (ex: subtrair 7 de 100) ou soletrar palavra de trás para frente.' },
  { id: 'recall', label: 'Evocação', max: 3, help: 'Lembrar as 3 palavras registradas anteriormente.' },
  { id: 'language', label: 'Linguagem', max: 8, help: 'Nomeação, repetição de frase, comando de 3 estágios, leitura, escrita de frase.' },
  { id: 'visuoconstruction', label: 'Capacidade visuoconstrutiva', max: 1, help: 'Cópia de um desenho geométrico (ex: pentágonos cruzados).' },
];

const brucki2003 = getEvidenceById('brucki-2003');
const lourencoVeras2006 = getEvidenceById('lourenco-veras-2006');

export const BRUCKI_2003_MEDIANS = brucki2003.results.educationMedians;

function cutoffPairToSides(group) {
  const [lowerSideMaximum, upperSideMinimum] = group.cutoffPair.split('/').map(Number);
  return {
    lowerSideMaximum,
    upperSideMinimum,
    sensitivity: group.sensitivity,
    specificity: group.specificity
  };
}

export const LOURENCO_VERAS_2006 = {
  noFormalEducation: cutoffPairToSides(lourencoVeras2006.results.groups.noFormalEducation),
  anyFormalEducation: cutoffPairToSides(lourencoVeras2006.results.groups.formalEducation)
};

export function calculateMiniMentalTotal(scores) {
  return Object.values(scores).reduce((acc, val) => acc + (parseInt(val, 10) || 0), 0);
}

export function validateDomainScore(value, max) {
  if (value === '') return null;
  const num = parseInt(value, 10);
  if (isNaN(num)) return 'Informe um número inteiro.';
  if (num < 0 || num > max) return `Informe um valor entre 0 e ${max}.`;
  return null;
}
/**
 * Faixas de escolaridade, com o equivalente escolar de cada uma.
 *
 * A correspondência entre anos de estudo e grau escolar é a do próprio estudo
 * de Brucki et al. (2003), que agrupou a amostra "de acordo com a grade escolar
 * em que a maioria se enquadrava": primário, ginásio, colegial e universitário.
 * Os nomes atuais aparecem ao lado porque são os que o paciente usa para
 * descrever a própria formação.
 */
export const EDUCATION_OPTIONS = [
  {
    value: 'noFormalEducation',
    label: 'Sem escolaridade formal',
    exemplo: 'Nunca frequentou a escola, ou frequentou sem concluir o primeiro ano.'
  },
  {
    value: 'years1To4',
    label: '1 a 4 anos de estudo',
    exemplo: 'Primário completo — hoje, anos iniciais do ensino fundamental (até o 5º ano).'
  },
  {
    value: 'years5To8',
    label: '5 a 8 anos de estudo',
    exemplo: 'Ginásio completo — hoje, ensino fundamental completo (até o 9º ano).'
  },
  {
    value: 'years9To11',
    label: '9 a 11 anos de estudo',
    exemplo: 'Colegial completo — hoje, ensino médio completo.'
  },
  {
    value: 'years12OrMore',
    label: '12 anos ou mais de estudo',
    exemplo: 'Ensino superior, completo ou em curso, e pós-graduação.'
  },
  {
    value: 'none',
    label: 'Prefiro não informar',
    exemplo: 'O total continua sendo somado, mas a comparação por escolaridade fica indisponível.'
  }
];

/** Referências de interpretação disponíveis, com a indicação de quando usar cada uma. */
export const REFERENCE_OPTIONS = [
  {
    value: 'brucki',
    label: 'Brucki et al., 2003 — medianas por escolaridade',
    recomendado: true,
    descricao:
      `A referência brasileira mais usada e a que padroniza a aplicação adotada aqui. Compara o total com a mediana observada no grupo de escolaridade correspondente, em ${brucki2003.sampleSize} pessoas saudáveis. Descreve desempenho: a mediana divide o grupo ao meio e não é ponto de corte diagnóstico.`
  },
  {
    value: 'lourenco',
    label: 'Lourenço e Veras, 2006 — divisão de rastreio',
    descricao:
      `Estudo com ${lourencoVeras2006.sampleSize} pessoas com mais de 65 anos, avaliadas em contexto ambulatorial. Traz divisões de rastreio com sensibilidade e especificidade medidas. Use quando quiser um limiar com desempenho psicométrico conhecido para uma população semelhante à estudada.`
  },
  {
    value: 'none',
    label: 'Apenas somar, sem comparação',
    descricao:
      'Mostra somente o total de 0 a 30. Use quando a comparação com referência populacional não se aplicar ao caso, ou quando o objetivo for apenas registrar a pontuação para acompanhamento do próprio paciente.'
  }
];
